/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Collapse, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack } from '@mui/material';

import { CredentialsError } from '../../../../components/CredentialsError/CredentialsError';
import { ForgotPasswordLink } from '../../../../components/LoginActions/ForgotPasswordLink';
import { OutlinedPasswordInput } from '../../../../elements/OutlinedPasswordInput/OutlinedPasswordInput';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { LoginErrorKey } from '../../../../types/login/LoginError';
import { createLoginPageStyles } from '../../LoginPage.styles';
import { LoginApiValidationErrors, LoginPageFormValues, ResetApiErrorForField } from '../../LoginPage.types';

type LoginFormProps = FormikProps<LoginPageFormValues> & {
  isFetching: boolean;
  apiErrors: LoginApiValidationErrors;
  resetApiError: ResetApiErrorForField;
  credentialsError?: LoginErrorKey;
  resetCredentialsError?: () => void;
  swapToSsoButton?: React.ReactNode;
};

export const LoginForm = memo<LoginFormProps>(props => {
  const classes = createLoginPageStyles();
  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.Login);
  const {
    values: { email, password },
    errors,
    touched,
    handleSubmit,
    handleChange,
    setFieldTouched,
    isFetching,
    apiErrors,
    resetApiError,
    credentialsError,
    resetCredentialsError,
    swapToSsoButton,
  } = props;

  const [continueWithBasic, setContinueWithBasic] = useState(!!email);

  const emailRef = useRef<any>(null);
  const passwordRef = useRef<any>(null);

  useEffect(() => {
    if (continueWithBasic && passwordRef.current) {
      setTimeout(() => passwordRef.current.focus(), 100);
    }
  }, [continueWithBasic]);

  const change = (name: keyof LoginPageFormValues, e: ChangeEvent) => {
    e.persist();
    handleChange(e);
    void setFieldTouched(name, true, false);
    resetApiError(name);

    if (['password', 'email'].includes(name) && credentialsError && resetCredentialsError) {
      resetCredentialsError();
    }
  };

  const hasError = (name: keyof LoginPageFormValues): boolean | undefined => {
    return touched[name] && !!(errors[name] || apiErrors[name]);
  };

  const getError = (name: keyof LoginPageFormValues): string | undefined => {
    const error = errors[name] || apiErrors[name];
    if (error) {
      return t(`validation.${name}.${error}`);
    }
    return undefined;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!continueWithBasic) {
      void setFieldTouched('email', true, true);
      if (!errors.email) {
        setContinueWithBasic(!continueWithBasic);
      }
    } else {
      handleSubmit();
    }
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    change('email', e);

    if (credentialsError && resetCredentialsError) {
      resetCredentialsError();
    }
  };

  const onEdit = () => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
    setContinueWithBasic(false);
  };

  return (
    <div className={classes.form}>
      <form onSubmit={onSubmit}>
        <div>
          <FormControl
            variant="outlined"
            className={cx(formClasses.formControl, formClasses.smallOffset)}
            error={hasError('email')}
          >
            <div className={classes.formGroup}>
              <InputLabel htmlFor="email">{t(`${Namespaces.Entities}:user.email.label`)}</InputLabel>
              <OutlinedInput
                autoFocus
                inputRef={emailRef}
                id="email"
                name="email"
                label={t(`${Namespaces.Entities}:user.email.label`)}
                error={hasError('email')}
                fullWidth
                type="email"
                value={email}
                onChange={onEmailChange}
                autoComplete="username"
                disabled={isFetching}
                data-cy="login-form-email-input"
                readOnly={continueWithBasic}
                endAdornment={
                  continueWithBasic && (
                    <Button size="small" onClick={onEdit}>
                      {t('common:form.edit')}
                    </Button>
                  )
                }
              />
              {hasError('email') && (
                <FormHelperText className={classes.formHelperTextPadding} data-cy="login-form-email-error">
                  {getError('email')}
                </FormHelperText>
              )}
            </div>
          </FormControl>
          <Collapse in={continueWithBasic}>
            <FormControl
              variant="outlined"
              className={cx(formClasses.formControl, formClasses.smallOffset)}
              error={hasError('password')}
            >
              <div className={classes.formGroup}>
                <InputLabel htmlFor="password">{t(`${Namespaces.Entities}:user.password.label`)}</InputLabel>
                <OutlinedPasswordInput
                  inputRef={passwordRef}
                  id="password"
                  name="password"
                  label={t(`${Namespaces.Entities}:user.password.label`)}
                  error={hasError('password')}
                  fullWidth
                  value={password}
                  onChange={change.bind(null, 'password')}
                  autoComplete="current-password"
                  disabled={isFetching}
                  data-cy="login-form-password-input"
                  showTooltip={t(`${Namespaces.Entities}:user.password.field.show`)}
                  hideTooltip={t(`${Namespaces.Entities}:user.password.field.hide`)}
                />
                {hasError('password') && (
                  <FormHelperText className={classes.formHelperTextPadding} data-cy="login-form-password-error">
                    {getError('password')}
                  </FormHelperText>
                )}
                {credentialsError && (
                  <CredentialsError show={!!credentialsError} errorKey={credentialsError} userEmail={email} />
                )}
              </div>
            </FormControl>
          </Collapse>
          <Button variant="contained" type="submit" fullWidth data-cy="continue-with-basic-button">
            {continueWithBasic
              ? t(`${Namespaces.Login}:page.login.login_button`)
              : t(`${Namespaces.Common}:stepper.next`)}
          </Button>
        </div>
      </form>
      <Stack spacing={1} sx={{ marginTop: theme => theme.spacing(2) }}>
        <ForgotPasswordLink email={email} />
        {swapToSsoButton}
      </Stack>
    </div>
  );
});
